/* Dr.Primal — capture step: the email form.

   Collects one email address and hands it to the CRM adapter (Klaviyo) through ctx.
   `mount()` resolves `{ email }` only when the subscription is accepted, or when the bot
   guard trips. Until then the visitor can correct the address and retry as often as
   they like.

   - Client-side format check first. A malformed address never leaves the page.
   - Bot guard (spec decision D2): a filled honeypot, or a submit less than MIN_FILL_MS
     after this step appeared, is treated exactly like a success. No network call, no
     error, and the flow carries on. The rule is `ctx.helpers.isBotSubmission`; only the
     threshold lives here.
   - In flight, the submit button is `disabled` and `aria-busy`, so a double click sends
     one request. The adapter times out on its own and reports `network`.
   - A refusal shows one of the two error lines Liquid rendered, paired with
     `aria-invalid` and `aria-describedby` on the field, sends `capture_submit_failed`
     with the adapter's reason, and gives the button back.
   - A subscribe call that REJECTS is a broken adapter, not a refusal. The step rejects,
     and the engine closes the dialog and records `capture_error`.
   - No user-facing English: this file only toggles `hidden` on lines Liquid wrote. */
(function () {
  'use strict';

  var MIN_FILL_MS = 2000;                       // a submit sooner than this after the step appears is treated as a bot (spec D2)
  var EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/; // the client-side format check; Klaviyo has the final say (400 → invalid)
  // Markup hooks. Each must match sections/dp-capture.liquid.
  var FORM_SELECTOR = '[data-dp-capture-form]';
  var EMAIL_SELECTOR = '[data-dp-capture-email]';
  var TRAP_SELECTOR = '[data-dp-capture-trap]';
  var SUBMIT_SELECTOR = '[data-dp-capture-submit]';
  var ERROR_ATTRIBUTE = 'data-dp-capture-error'; // its values, 'invalid' and 'failed', are the kinds errorFor() returns
  var DISABLED_CLASS = 'dp-button--disabled';   // dp-button's dimmed state; `disabled` alone looks untouched

  var capture = window.dpCapture;
  if (!capture) {
    console.error('[dp-capture-step-email] dp-capture.js must load before this file');
    return;
  }

  function isEmail(value) {
    return EMAIL_PATTERN.test(value);
  }

  /* crm.subscribe's outcome → which error line to show: '' (none), 'invalid' or 'failed'. */
  function errorFor(result) {
    if (result && result.ok) return '';
    return result && result.reason === 'invalid_email' ? 'invalid' : 'failed';
  }

  function emailStep(root, ctx) {
    var form = root.querySelector(FORM_SELECTOR);
    var input = root.querySelector(EMAIL_SELECTOR);
    var trap = root.querySelector(TRAP_SELECTOR);
    var submit = root.querySelector(SUBMIT_SELECTOR);
    var errors = root.querySelectorAll('[' + ERROR_ATTRIBUTE + ']');

    var startedAt = 0;
    var busy = false;
    var listeners = [];

    function on(target, type, handler) {
      target.addEventListener(type, handler);
      listeners.push([target, type, handler]);
    }

    function showError(kind) {
      var shown = null;
      Array.prototype.forEach.call(errors, function (node) {
        var match = node.getAttribute(ERROR_ATTRIBUTE) === kind;
        node.hidden = !match;
        if (match) shown = node;
      });
      if (shown) {
        input.setAttribute('aria-invalid', 'true');
        input.setAttribute('aria-describedby', shown.id);
      } else {
        input.removeAttribute('aria-invalid');
        input.removeAttribute('aria-describedby');
      }
    }

    function setBusy(on) {
      busy = on;
      submit.disabled = on;
      submit.classList.toggle(DISABLED_CLASS, on);
      if (on) submit.setAttribute('aria-busy', 'true');
      else submit.removeAttribute('aria-busy');
    }

    return {
      mount: function () {
        startedAt = window.performance.now();

        return new Promise(function (resolve, reject) {
          // Typing again clears a shown error, so the field stops saying it is invalid.
          on(input, 'input', function () {
            if (input.hasAttribute('aria-invalid')) showError('');
          });

          on(form, 'submit', function (event) {
            event.preventDefault();
            if (busy) return;

            var email = input.value.trim();
            if (!isEmail(email)) {
              showError('invalid');
              input.focus();
              return;
            }
            showError('');

            var bot = ctx.helpers.isBotSubmission({
              honeypot: trap ? trap.value : '',
              elapsedMs: window.performance.now() - startedAt,
              minFillMs: MIN_FILL_MS,
            });
            if (bot) {
              resolve({ email: email });
              return;
            }

            setBusy(true);
            var request;
            try {
              request = Promise.resolve(ctx.adapters.crm.subscribe(ctx.helpers.subscriptionFor(email)));
            } catch (error) {
              request = Promise.reject(error);
            }
            request.then(
              function (result) {
                setBusy(false);
                var kind = errorFor(result);
                if (!kind) {
                  resolve({ email: email });
                  return;
                }
                ctx.track(ctx.events.SUBMIT_FAILED, { reason: (result && result.reason) || 'network' });
                showError(kind);
                input.focus();
              },
              function (error) {
                setBusy(false);
                reject(error);
              }
            );
          });
        });
      },
      unmount: function () {
        listeners.forEach(function (entry) {
          entry[0].removeEventListener(entry[1], entry[2]);
        });
        listeners = [];
      },
    };
  }

  // The pure parts, for tests/dp-capture/. The theme never calls these directly.
  emailStep.core = { isEmail: isEmail, errorFor: errorFor, MIN_FILL_MS: MIN_FILL_MS };

  capture.registerStep('email', emailStep);
})();
